import { createGetSelfInfoOutbound } from '../../api/outbound/commonOutbounds';
import { isSupportedPlayerServerSettingType } from '../../types/playerServerSettingsTypes';
import { maybeOverrideAvatar } from '../../utils/avatarUtils';
import { setBugsnagUser } from '../../utils/bugsnagUtils';
import { getIsColumnLayoutMode } from '../../utils/layoutModeUtils';
import { assertUnreachable } from '../../utils/tsUtils';
import { loadUserClientSettings, saveUserClientSetting } from '../../utils/userClientSettingsUtils';
import { uuid } from '../../utils/utils';
import { selectIsSideBetsPanelEnabled, selectPanelPositionSetting } from '../reducers/selfDataReducer';
import { setIsColumnLayoutMode } from './commonActions';
import { setMyPlayerIdInMttStaging } from './mttStagingActions';
import { setSideBetsPanelState, thunkSetActivePanel } from './panelsActions';
import { setMyPlayerIdInPokerScene } from './pokerActions';
export const CLEAR_SELF_DATA_STATE = 'CLEAR_SELF_DATA_STATE';
export const SET_SELF_DATA_FROM_INBOUND = 'SET_SELF_DATA_FROM_INBOUND';
export const UPDATE_JACKS_AND_AMMO_FROM_INBOUND = 'UPDATE_JACKS_AND_AMMO_FROM_INBOUND';
export const SET_SELF_CHIPS = 'SET_SELF_CHIPS';
export const SET_SELF_POWER_PLAYER_STATUS = 'SET_SELF_POWER_PLAYER_STATUS';
export const SET_USER_CLIENT_SETTINGS = 'SET_USER_CLIENT_SETTINGS';
export const SET_PLAYER_SERVER_SETTINGS_FROM_INBOUND = 'SET_PLAYER_SERVER_SETTINGS_FROM_INBOUND';
export const UPDATE_PLAYER_SERVER_SETTING_FROM_INBOUND = 'UPDATE_PLAYER_SERVER_SETTING_FROM_INBOUND';
export const SET_IS_SELF_INFO_LOADING = 'SET_IS_SELF_INFO_LOADING';
export const SET_FETCHED_SELF_INFO = 'SET_FETCHED_SELF_INFO';
export const REBUY_SUCCESS_FROM_INBOUND = 'REBUY_SUCCESS_FROM_INBOUND';
export const CLEAR_REBUY_DELAY = 'CLEAR_REBUY_DELAY';
export const UPDATE_FREE_DAILY_BONUS_DELAY = 'UPDATE_FREE_DAILY_BONUS_DELAY';
export function clearSelfDataState() {
    return {
        type: CLEAR_SELF_DATA_STATE
    };
}
function setSelfData(inboundSelfData) {
    return {
        type: SET_SELF_DATA_FROM_INBOUND,
        payload: inboundSelfData
    };
}
function thunkLoadUserClientSettings(playerId) {
    return function (dispatch, getState) {
        dispatch(setUserClientSettings(loadUserClientSettings(playerId)));
        dispatch(setIsColumnLayoutMode(getIsColumnLayoutMode(selectPanelPositionSetting(getState()))));
    };
}
export const thunkSetSelfDataFromInbound = (inboundSelfData) => {
    return function (dispatch, getState) {
        const state = getState();
        const currentPlayerId = state.selfData.id;
        if (currentPlayerId === undefined) {
            // player is being set for the first time this login session
            setBugsnagUser(inboundSelfData.username, inboundSelfData.id); // TODO: Clear Bugsnag user if it becomes possible to log out without refreshing the app/page.
            dispatch(thunkLoadUserClientSettings(inboundSelfData.id));
        }
        dispatch(setSelfData(inboundSelfData));
        if (!state.scene.roomSceneType) {
            return;
        }
        switch (state.scene.roomSceneType) {
            case 'poker': {
                dispatch(setMyPlayerIdInPokerScene(inboundSelfData.id));
                break;
            }
            case 'mttStaging': {
                dispatch(setMyPlayerIdInMttStaging(inboundSelfData.id));
                break;
            }
            case 'lobby': {
                // TODO: Track myPlayerId in lobby (I expect it will be needed)
                break;
            }
            default: {
                assertUnreachable(state.scene.roomSceneType);
            }
        }
    };
};
export function updateJacksAndAmmoFromInbound(inbound) {
    return {
        type: UPDATE_JACKS_AND_AMMO_FROM_INBOUND,
        payload: inbound
    };
}
export function setSelfChips(numChips) {
    return {
        type: SET_SELF_CHIPS,
        payload: numChips
    };
}
export function setSelfPowerPlayerStatus(isPowerPlayer) {
    return {
        type: SET_SELF_POWER_PLAYER_STATUS,
        payload: isPowerPlayer
    };
}
function thunkSaveUserClientSetting(settingKey, settingValue) {
    return function (dispatch, getState) {
        const playerId = getState().selfData.id;
        if (playerId === undefined) {
            return;
        }
        saveUserClientSetting(playerId, settingKey, settingValue);
        dispatch(thunkLoadUserClientSettings(playerId));
    };
}
export function thunkSetSoundVolume(soundVolume) {
    return function (dispatch) {
        const newSoundVolume = Math.max(-100, Math.min(100, soundVolume));
        dispatch(thunkSaveUserClientSetting('SOUND_VOLUME', { percent: newSoundVolume }));
    };
}
export function thunkSetLastNewsItemHash(lastNewsItemHash) {
    return function (dispatch) {
        dispatch(thunkSaveUserClientSetting('LAST_NEWS_ITEM_HASH', { value: lastNewsItemHash }));
    };
}
export function thunkSetPanelPositionSetting(panelPositionSetting) {
    return function (dispatch) {
        dispatch(thunkSaveUserClientSetting('PANEL_POSITION', { position: panelPositionSetting }));
        dispatch(setIsColumnLayoutMode(getIsColumnLayoutMode(panelPositionSetting)));
    };
}
function setUserClientSettings(userClientSettings) {
    return {
        type: SET_USER_CLIENT_SETTINGS,
        payload: userClientSettings
    };
}
export function setPlayerServerSettingsFromInbound(inboundPlayerServerSettings) {
    return {
        type: SET_PLAYER_SERVER_SETTINGS_FROM_INBOUND,
        payload: inboundPlayerServerSettings
    };
}
function updatePlayerServerSettingFromInbound(inbound) {
    return {
        type: UPDATE_PLAYER_SERVER_SETTING_FROM_INBOUND,
        payload: inbound
    };
}
/**
 * Responds to specific player setting changes.
 */
function maybeDispatchSpecificPlayerServerSetting({ type, currentValueIndex }, dispatch, getState, socketSend) {
    if (!isSupportedPlayerServerSettingType(type)) {
        return;
    }
    const state = getState();
    switch (type) {
        case 'sidebets_enabled': {
            if (state.scene.roomSceneType !== 'poker') {
                // side bet setting changes are only relevant to the UI in poker scene
                return;
            }
            if (!selectIsSideBetsPanelEnabled(state)) {
                return;
            }
            const isEnabled = currentValueIndex === 1;
            if (!isEnabled && state.panels.activePanelType === 'sideBets') {
                // disabling side bets panel, which is the active panel, so switch to chat
                dispatch(thunkSetActivePanel('chat', socketSend, false));
            }
            dispatch(setSideBetsPanelState(isEnabled));
        }
    }
}
export function thunkUpdatePlayerServerSettingFromInbound(inbound, socketSend) {
    return function (dispatch, getState) {
        dispatch(updatePlayerServerSettingFromInbound(inbound));
        if (inbound.inboundPlayerServerSetting.type)
            maybeDispatchSpecificPlayerServerSetting(inbound.inboundPlayerServerSetting, dispatch, getState, socketSend);
    };
}
export function setIsSelfInfoLoading(isLoading) {
    return {
        type: SET_IS_SELF_INFO_LOADING,
        payload: isLoading
    };
}
function setFetchedSelfInfo(fetchedSelfInfo) {
    return {
        type: SET_FETCHED_SELF_INFO,
        payload: fetchedSelfInfo
    };
}
export const thunkShowSelfInfoFromInbound = (inbound) => {
    return function (dispatch, getState) {
        const state = getState();
        const defaultPlayerAvatar = inbound.avatarPropertiesSet
            ? { type: 'custom', avatar: inbound.avatarPropertiesSet, uuid: uuid() }
            : undefined;
        const playerAvatar = state.selfData.id && state.selfData.username
            ? maybeOverrideAvatar(state.selfData.id, state.selfData.username, defaultPlayerAvatar)
            : defaultPlayerAvatar;
        const fetchedSelfInfo = {
            isPowerPlayer: inbound.isPowerPlayer,
            numChipsInBank: inbound.numChipsInBank,
            playerAvatar,
            medals: inbound.medals,
            tickets: inbound.tickets,
            playerStyle: inbound.playerStyle,
            bio: inbound.bio,
            statsFields: inbound.statsFields
        };
        dispatch(setFetchedSelfInfo(fetchedSelfInfo));
    };
};
export const thunkShowSelfInfo = (socketSend) => {
    return function (dispatch) {
        dispatch(setIsSelfInfoLoading(true));
        socketSend(createGetSelfInfoOutbound());
    };
};
export function rebuySuccessFromInbound(inbound) {
    return {
        type: REBUY_SUCCESS_FROM_INBOUND,
        payload: inbound
    };
}
export function clearRebuyDelay() {
    return {
        type: CLEAR_REBUY_DELAY
    };
}
export function updateFreeDailyBonusDelay(freeDailyBonusSecondsLeft) {
    return {
        type: UPDATE_FREE_DAILY_BONUS_DELAY,
        payload: freeDailyBonusSecondsLeft
    };
}
